import { NotificationChannel, convertLanguage } from '@frontend/common';
import { Spinner } from '@frontend/components/common';
import { ICONS } from '@frontend/icons';
import { NotificationContent } from '@frontend/notification/types';
import type * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { buildPreviewDocument } from '../editor.controller';
import { NotificationChannelLabel } from '../notification-channel-label';
import { useContentPreview } from './content-preview.controller';

export interface ContentPreviewProps {
    content: NotificationContent;
    draft: { subject: string; body: string } | null;
    placeholderValues?: Record<string, string>;
    onClose: () => void;
    showClose?: boolean;
}

export const ContentPreview = (props: ContentPreviewProps) => {
    const viewProps = useContentPreview(props);
    const intl = useIntl();

    if (!viewProps.body && viewProps.isLoading) {
        return (
            <div className='bg-background-surface outline-border-default flex h-full flex-col items-center justify-center self-stretch rounded-md outline-1 outline-offset-[-1px]'>
                <Spinner />
            </div>
        );
    }
    return (
        <div className='bg-background-surface outline-border-default flex h-full max-h-[calc(100vh-8rem)] w-full flex-col items-start justify-start overflow-hidden rounded-xl outline-1 outline-offset-[-1px]'>
            <div className='inline-flex flex-col items-start justify-start gap-6 self-stretch rounded-tl-2xl rounded-tr-2xl px-4 pt-4 pb-3'>
                <div className='flex w-full flex-row items-center justify-between'>
                    <div className='text-foreground-default justify-start text-2xl font-semibold'>
                        {viewProps.isLocalDraftPreview ? (
                            <FormattedMessage
                                id='content-preview.preview-draft'
                                description='Heading shown when previewing unsaved notification content locally'
                                defaultMessage='Preview draft'
                            />
                        ) : (
                            <FormattedMessage
                                id='content-preview.preview-saved'
                                description='Heading shown when previewing saved backend-rendered notification content'
                                defaultMessage='Preview saved version'
                            />
                        )}
                    </div>
                    {props.showClose !== false && (
                        <div
                            role='button'
                            tabIndex={0}
                            aria-label={intl.formatMessage({
                                id: 'content-preview.close',
                                description: 'Accessible label for closing the notification content preview modal',
                                defaultMessage: 'Close preview'
                            })}
                            className='cursor-pointer'
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    props.onClose();
                                }
                            }}
                            onClick={props.onClose}>
                            <ICONS.BUTTON.CLOSE_2 className='size-8' />
                        </div>
                    )}
                </div>

                <div className='border-border-default flex w-full flex-col gap-2 rounded-md border bg-white p-3'>
                    <div className='text-foreground-default text-base font-semibold'>{viewProps.subject}</div>
                    <div className='text-brand-muted flex flex-wrap gap-x-4 gap-y-1 text-xs'>
                        <span>
                            <FormattedMessage
                                id='content-preview.channel'
                                description='Label for the notification channel in the preview metadata'
                                defaultMessage='Channel'
                            />
                            {': '}
                            <NotificationChannelLabel channel={props.content.channel} />
                        </span>
                        <span>
                            <FormattedMessage
                                id='content-preview.language'
                                description='Label for the notification language in the preview metadata'
                                defaultMessage='Language'
                            />
                            {': '}
                            {convertLanguage(props.content.language)}
                        </span>
                    </div>
                </div>
                {viewProps.isLocalDraftPreview && (
                    <div className='bg-status-info/10 text-foreground-default rounded-md px-3 py-2 text-sm'>
                        <FormattedMessage
                            id='content-preview.local-draft-preview'
                            description='Notice shown when previewing unsaved notification editor content locally with sample placeholder values'
                            defaultMessage='Previewing unsaved content locally. Placeholders are filled with sample values.'
                        />
                    </div>
                )}
                {viewProps.hasPreviewError && (
                    <div className='bg-status-warning/10 text-status-warning rounded-md px-3 py-2 text-sm'>
                        <FormattedMessage
                            id='content-preview.backend-preview-error'
                            description='Warning shown when the backend notification preview cannot be loaded and the saved content is rendered locally instead'
                            defaultMessage='The backend preview could not be loaded. Showing saved content without injected placeholder values.'
                        />
                    </div>
                )}
            </div>
            <div className='flex min-h-0 flex-1 flex-col self-stretch overflow-y-auto'>
                <div className='flex items-center gap-2 self-stretch px-4 pb-3'>
                    <PreviewViewportButton
                        active={viewProps.viewport === 'desktop'}
                        onClick={() => viewProps.setViewport('desktop')}>
                        <FormattedMessage
                            id='content-preview.desktop'
                            description='Button label for desktop-width notification content preview'
                            defaultMessage='Desktop'
                        />
                    </PreviewViewportButton>
                    <PreviewViewportButton
                        active={viewProps.viewport === 'mobile'}
                        onClick={() => viewProps.setViewport('mobile')}>
                        <FormattedMessage
                            id='content-preview.mobile'
                            description='Button label for mobile-width notification content preview'
                            defaultMessage='Mobile'
                        />
                    </PreviewViewportButton>
                </div>
                <div className='flex justify-center self-stretch px-4 pb-4'>
                    <iframe
                        title={intl.formatMessage({
                            id: 'content-preview.iframe-title',
                            description: 'Accessible title for the iframe that renders notification content preview HTML',
                            defaultMessage: 'Notification content preview'
                        })}
                        className={`border-border-default h-[70vh] min-h-[420px] rounded-md border bg-white transition-[width] ${viewProps.viewport === 'mobile' ? 'w-[390px] max-w-full' : 'w-full'}`}
                        sandbox='allow-popups allow-popups-to-escape-sandbox'
                        srcDoc={buildPreviewDocument(viewProps.body, props.content.channel !== NotificationChannel.EMAIL)}
                    />
                </div>
            </div>
        </div>
    );
};

const PreviewViewportButton = (props: { active: boolean; onClick(): void; children: React.ReactNode }) => (
    <button
        type='button'
        className={`${props.active ? 'bg-brand-primary text-background-surface border-brand-primary' : 'text-brand-primary border-border-default'} hover:border-brand-secondary flex h-9 items-center justify-center rounded-md border px-3 text-sm font-medium`}
        onClick={props.onClick}>
        {props.children}
    </button>
);
